package com.example.crashbit

import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.geometry.Size
import androidx.compose.ui.graphics.*
import androidx.compose.ui.graphics.drawscope.DrawScope
import androidx.compose.ui.graphics.drawscope.Stroke
import androidx.compose.ui.graphics.drawscope.withTransform
import kotlin.math.*
import kotlin.random.Random

object SpaceRenderer {

    private val spaceBlack = Color(0xFF05000A)
    private val starColors = arrayOf(
        Color(0xFF9BB0FF),
        Color(0xFFAABFFF),
        Color(0xFFCAD7FF),
        Color(0xFFF8F7FF),
        Color(0xFFFFF4EA),
        Color(0xFFFFD2A1),
        Color(0xFFFFCC6F)
    )

    // Pre-allocated lists for gradients to avoid frame allocations
    private val sweepGradientColors = listOf(
        Color.Transparent,
        Color.Black.copy(alpha = 0.35f),
        Color.Transparent,
        Color.White.copy(alpha = 0.2f),
        Color.Black.copy(alpha = 0.25f),
        Color.Transparent
    )

    private val bgGradientColors = listOf(spaceBlack, Color.Black)
    private val flareWhiteColors = listOf(Color.White, Color.Transparent)

    fun lerp(start: Offset, end: Offset, alpha: Float): Offset {
        return Offset(start.x + (end.x - start.x) * alpha, start.y + (end.y - start.y) * alpha)
    }

    fun lerpRotation(start: Float, end: Float, alpha: Float): Float {
        return start + (end - start) * alpha
    }

    fun DrawScope.drawBackgroundStars(
        backgroundStars: List<Offset>,
        pan: Offset,
        scale: Float
    ) {
        drawRect(
            brush = Brush.radialGradient(
                colors = bgGradientColors,
                center = center,
                radius = size.maxDimension
            )
        )
        
        val fieldSize = 3000f
        val offsetX = (pan.x * scale * 0.15f) % fieldSize
        val offsetY = (pan.y * scale * 0.15f) % fieldSize

        backgroundStars.forEach { starPos ->
            var x = (starPos.x + offsetX) % fieldSize
            var y = (starPos.y + offsetY) % fieldSize
            if (x < 0) x += fieldSize
            if (y < 0) y += fieldSize

            val starRadius = (1.2f / scale.coerceAtLeast(0.5f)).coerceIn(0.5f, 1.2f)
            drawCircle(
                color = Color.White.copy(alpha = 0.45f),
                radius = starRadius,
                center = Offset(x % size.width, y % size.height)
            )
        }
    }

    fun DrawScope.drawVisualEffects(
        activeEffects: List<VisualEffect>,
        stars: List<SpaceBody>,
        scale: Float,
        gameTimeNanos: Long,
        interpolationAlpha: Float = 1.0f
    ) {
        val nowGameMs = gameTimeNanos / 1_000_000
        val maxScreenRadius = size.maxDimension * 1.5f

        activeEffects.forEach { effect ->
            val elapsed = nowGameMs - effect.startTime
            val progress = (elapsed.toFloat() / effect.durationMs.coerceAtLeast(1)).coerceIn(0f, 1f)
            val fade = (1.0f - progress).coerceIn(0f, 1f)

            when (effect.type) {
                EffectType.GRAVITATIONAL_WAVE -> {
                    for (i in 1..3) {
                        val ringRadius = ((progress * 3000f * i) + (i * 50f)).coerceAtLeast(1f)
                        if (ringRadius > maxScreenRadius) continue
                        val cappedWidth = minOf(2f * fade * effect.intensity, 15f / scale.coerceAtLeast(0.5f)).coerceAtLeast(1f)
                        drawCircle(
                            color = Color.White.copy(alpha = fade * 0.15f),
                            radius = ringRadius,
                            center = effect.position,
                            style = Stroke(width = cappedWidth)
                        )
                    }
                }
                EffectType.STAR_COLLAPSE -> {
                    val radius = (progress * 6000f).coerceIn(1f, maxScreenRadius)
                    drawCircle(
                        brush = Brush.radialGradient(
                            0.0f to Color.White, 0.3f to Color.Yellow, 0.7f to Color(0xFFFF8C00), 1.0f to Color.Transparent,
                            center = effect.position,
                            radius = radius
                        ),
                        radius = radius,
                        center = effect.position,
                        alpha = fade
                    )
                }
                EffectType.BLACK_HOLE_COLLAPSE -> {
                    val radius = (progress * 10000f).coerceIn(1f, maxScreenRadius)
                    drawCircle(
                        color = (if (progress < 0.5f) Color.White else Color.Black).copy(alpha = fade),
                        radius = radius,
                        center = effect.position
                    )
                }
                EffectType.ACCRETION_BURST -> {
                    val radius = (progress * 1500f).coerceIn(1f, maxScreenRadius)
                    drawCircle(
                        brush = Brush.radialGradient(
                            0.0f to Color.White, 0.5f to Color(0xFF8B00FF), 1.0f to Color.Transparent,
                            center = effect.position,
                            radius = radius
                        ),
                        radius = radius,
                        center = effect.position,
                        alpha = fade * 0.8f,
                        blendMode = BlendMode.Plus
                    )
                }
                EffectType.SOLAR_FLARE -> {
                    val parentStar = stars.find { it.id == effect.parentId } ?: stars.find { (it.renderPosition - effect.position).getDistance() < 10f }
                    val currentCenter = parentStar?.let { lerp(it.previousPosition, it.renderPosition, interpolationAlpha) } ?: effect.position
                    val baseAngleRad = (effect.intensity * PI / 180.0)
                    val rotationOffsetRad = parentStar?.let { (lerpRotation(it.previousRotation, it.rotation, interpolationAlpha) * PI / 180.0) } ?: 0.0
                    val finalAngleRad = baseAngleRad + rotationOffsetRad

                    val currentRadius = parentStar?.radius ?: 120f
                    val massFactor = (parentStar?.mass ?: 100000f) / 100000f
                    val isRare = effect.color == Color.White
                    val isSuperRare = effect.color == Color.Magenta
                    val baseReachMultiplier = if (isSuperRare) 50.0f else if (isRare) 25.0f else 7.5f
                    val maxReach = currentRadius * baseReachMultiplier * massFactor * (if (scale < 0.1f) 1.2f else 1.0f)
                    val flareReach = (maxReach * (1.0f - (progress - 0.5f) * (progress - 0.5f) * 4)).coerceAtLeast(0.1f)
                    
                    val startPos = currentCenter + Offset(cos(finalAngleRad - 0.15).toFloat() * currentRadius, sin(finalAngleRad - 0.15).toFloat() * currentRadius)
                    val endPos = currentCenter + Offset(cos(finalAngleRad + 0.15).toFloat() * currentRadius, sin(finalAngleRad + 0.15).toFloat() * currentRadius)
                    val midPos = currentCenter + Offset(cos(finalAngleRad).toFloat() * (currentRadius + flareReach), sin(finalAngleRad).toFloat() * (currentRadius + flareReach))
                    
                    val flarePath = Path().apply {
                        moveTo(startPos.x, startPos.y)
                        quadraticTo(midPos.x, midPos.y, endPos.x, endPos.y)
                    }
                    val flicker = (sin(gameTimeNanos.toDouble() / (if (isSuperRare) 6e6 else if (isRare) 1e7 else 2e7)).toFloat() + 1f) / 2f
                    val renderColor = if (isSuperRare) Color.Cyan else if (isRare) Color.Yellow else effect.color
                    val finalWidth = ( (if (isSuperRare) 25f else if (isRare) 18f else 12f) + 4f * flicker) / scale.coerceAtLeast(0.05f)
                    
                    if (finalWidth > 0.05f) {
                        drawPath(path = flarePath, color = renderColor.copy(alpha = fade * 0.9f), style = Stroke(width = finalWidth, cap = StrokeCap.Round), blendMode = BlendMode.Plus)
                        if (isRare || isSuperRare) {
                            drawPath(path = flarePath, color = Color.White.copy(alpha = fade * 0.8f), style = Stroke(width = finalWidth * 0.4f, cap = StrokeCap.Round), blendMode = BlendMode.Plus)
                        }
                    }
                }
                EffectType.SUPERNOVA_SHOCKWAVE -> {
                    val radius = (progress * 6000f).coerceIn(1f, maxScreenRadius)
                    drawCircle(
                        color = effect.color.copy(alpha = fade * 0.6f),
                        radius = radius,
                        center = effect.position,
                        style = Stroke(width = (20f / scale).coerceIn(2f, 50f))
                    )
                    if (progress < 0.2f) {
                        drawCircle(color = Color.White.copy(alpha = 1.0f - progress / 0.2f), radius = (2000f / scale).coerceIn(1f, maxScreenRadius), center = effect.position)
                    }
                    drawCircle(
                        brush = Brush.radialGradient(
                            0.0f to effect.color.copy(alpha = fade * 0.3f), 1.0f to Color.Transparent,
                            center = effect.position,
                            radius = radius * 0.8f
                        ),
                        radius = radius * 0.8f,
                        center = effect.position,
                        blendMode = BlendMode.Screen
                    )
                }
                else -> {}
            }
        }
    }

    fun DrawScope.drawStars(
        stars: List<SpaceBody>,
        scale: Float,
        gameTimeNanos: Long,
        interpolationAlpha: Float = 1.0f
    ) {
        stars.forEach { star ->
            val currentPos = lerp(star.previousPosition, star.renderPosition, interpolationAlpha)
            if (star.isBlackHole) {
                val effRad = star.radius.coerceAtLeast(1.92f / scale)
                val auraPulse = (sin(gameTimeNanos.toDouble() / 200_000_000.0).toFloat() + 1f) / 2f
                drawCircle(
                    brush = Brush.radialGradient(
                        0.0f to Color.Black, 0.4f to Color(0xFF8B00FF).copy(alpha = 0.3f), 1.0f to Color.Transparent,
                        center = currentPos,
                        radius = (effRad * 4f * (0.9f + 0.1f * auraPulse)).coerceAtLeast(1f)
                    ),
                    radius = (effRad * 4f * (0.9f + 0.1f * auraPulse)).coerceAtLeast(1f),
                    center = currentPos
                )
                val accPulse = (sin(gameTimeNanos.toDouble() / 150_000_000.0).toFloat() + 1f) / 2f
                drawCircle(
                    brush = Brush.radialGradient(
                        0.0f to Color(0xFFFF0000).copy(alpha = 0.8f + 0.2f * accPulse), 0.6f to Color(0xFFFF8C00).copy(alpha = 0.4f), 1.0f to Color.Transparent,
                        center = currentPos,
                        radius = (effRad * 6f).coerceAtLeast(1f)
                    ),
                    radius = (effRad * 6f).coerceAtLeast(1f),
                    center = currentPos
                )
                drawCircle(
                    brush = Brush.radialGradient(
                        0.0f to Color.Black, 0.5f to Color(0xFF8B00FF), 0.8f to Color.Cyan, 1.0f to Color.Transparent,
                        center = currentPos,
                        radius = (effRad * 2.5f).coerceAtLeast(1f)
                    ),
                    radius = (effRad * 2.5f).coerceAtLeast(1f),
                    center = currentPos
                )
                drawCircle(color = Color.Black, radius = effRad, center = currentPos)

                if (star.mass >= 3000000f) {
                    val growth = ((star.mass - 3000000f) / 500000f).coerceAtLeast(0f)
                    val jetFlicker = (sin(gameTimeNanos.toDouble() / 30_000_000.0).toFloat() + 1f) / 2f
                    val jetL = (star.radius * 8f + (growth * star.radius * 1.5f)).coerceAtMost(10000f)
                    val jetW = star.radius * (0.7f + growth * 0.05f).coerceAtMost(2.0f)
                    
                    val jetBrushTop = Brush.linearGradient(listOf(Color(0xFF8B00FF), Color.Cyan, Color.Transparent), start = currentPos, end = currentPos - Offset(0f, jetL))
                    val jetBrushBottom = Brush.linearGradient(listOf(Color(0xFF8B00FF), Color.Cyan, Color.Transparent), start = currentPos, end = currentPos + Offset(0f, jetL))
                    
                    drawPath(Path().apply {
                        moveTo(currentPos.x - jetW/2, currentPos.y - star.radius * 0.5f)
                        lineTo(currentPos.x + jetW/2, currentPos.y - star.radius * 0.5f)
                        lineTo(currentPos.x, currentPos.y - jetL)
                        close()
                    }, brush = jetBrushTop, alpha = 0.4f + 0.3f * jetFlicker)
                    
                    drawPath(Path().apply {
                        moveTo(currentPos.x - jetW/2, currentPos.y + star.radius * 0.5f)
                        lineTo(currentPos.x + jetW/2, currentPos.y + star.radius * 0.5f)
                        lineTo(currentPos.x, currentPos.y + jetL)
                        close()
                    }, brush = jetBrushBottom, alpha = 0.4f + 0.3f * jetFlicker)
                }
            } else {
                val starColor = when {
                    star.temperature >= 25000f -> starColors[0]
                    star.temperature >= 10000f -> starColors[1]
                    star.temperature >= 7500f  -> starColors[2]
                    star.temperature >= 6000f  -> starColors[3]
                    star.temperature >= 5000f  -> starColors[4]
                    star.temperature >= 3500f  -> starColors[5]
                    else                       -> starColors[6]
                }
                val effRad = (star.radius + sin(gameTimeNanos.toDouble() / 2e8).toFloat() * 3f).coerceAtLeast(3.84f / scale)
                val dynCorona = if (scale > 1f) 1.1f else (1.1f + (1.5f / scale.coerceIn(0.15f, 1f) - 1.1f) * (1f - scale).coerceIn(0f, 1f))
                
                drawCircle(
                    brush = Brush.radialGradient(
                        0.0f to starColor.copy(alpha = 0.4f), 0.5f to starColor.copy(alpha = 0.1f), 1.0f to Color.Transparent,
                        center = currentPos,
                        radius = (effRad * 4f * dynCorona).coerceAtLeast(1f)
                    ),
                    radius = (effRad * 4f * dynCorona).coerceAtLeast(1f),
                    center = currentPos,
                    blendMode = BlendMode.Plus
                )
                drawCircle(
                    brush = Brush.radialGradient(
                        0.0f to Color.White, 0.4f to starColor, 0.8f to Color(0xFFFF8C00), 1.0f to Color.Transparent,
                        center = currentPos,
                        radius = (effRad * dynCorona).coerceAtLeast(1f)
                    ),
                    radius = (effRad * dynCorona).coerceAtLeast(1f),
                    center = currentPos
                )
                drawCircle(color = Color.White, radius = effRad * 0.6f, center = currentPos)

                val rng = Random(star.id)
                val spikeL = effRad * 4.5f
                val spikeW = 1.5f / scale.coerceAtLeast(0.5f)
                val baseA = rng.nextFloat() * 360f
                for (aOff in arrayOf(0f, 90f)) {
                    val aR = ((baseA + aOff) * PI / 180.0)
                    val dir = Offset(cos(aR).toFloat(), sin(aR).toFloat())
                    drawLine(
                        brush = Brush.linearGradient(
                            0.0f to Color.Transparent, 0.5f to Color.White.copy(alpha = 0.6f), 1.0f to Color.Transparent,
                            start = currentPos - dir * spikeL,
                            end = currentPos + dir * spikeL
                        ),
                        start = currentPos - dir * spikeL, end = currentPos + dir * spikeL, strokeWidth = spikeW
                    )
                }

                stars.forEach { other ->
                    if (star.id < other.id) {
                        val otherPos = lerp(other.previousPosition, other.renderPosition, interpolationAlpha)
                        val dist = (currentPos - otherPos).getDistance()
                        if (dist < (star.radius + other.radius) * 3.5f && dist > (star.radius + other.radius)) {
                            val mid = (currentPos + otherPos) / 2f
                            drawCircle(
                                brush = Brush.radialGradient(
                                    listOf(Color.White.copy(alpha = 0.15f), Color.Transparent),
                                    center = mid,
                                    radius = dist * 0.5f
                                ),
                                radius = dist * 0.5f, center = mid, blendMode = BlendMode.Plus
                            )
                        }
                    }
                }
            }
        }
    }

    fun DrawScope.drawHabitableZones(stars: List<SpaceBody>, scale: Float, interpolationAlpha: Float = 1.0f) {
        stars.forEach { star ->
            if (!star.isBlackHole) {
                val currentPos = lerp(star.previousPosition, star.renderPosition, interpolationAlpha)
                drawCircle(Color.Green.copy(alpha = 0.05f), radius = 900f, center = currentPos)
                drawCircle(Color.Green.copy(alpha = 0.15f), radius = 900f, center = currentPos, style = Stroke(width = 20f / scale))
                drawCircle(Color.Yellow.copy(alpha = 0.15f), radius = 400f, center = currentPos, style = Stroke(width = 5f / scale))
            }
        }
    }

    fun DrawScope.drawTrajectories(oracle: OracleResult?, stats: TelemetryStats, scale: Float) {
        oracle?.paths?.forEachIndexed { idx, points ->
            if (points.size > 1) {
                val isF = idx == stats.fastestBodyId
                val path = Path().apply {
                    moveTo(points[0].x, points[0].y)
                    for (i in 1 until points.size) lineTo(points[i].x, points[i].y)
                }
                val width = (if (isF) 3.0f else 1.5f) / scale
                if (width > 0.05f) {
                    val brush = Brush.linearGradient(
                        colors = if (isF) listOf(Color.Cyan.copy(alpha = 0.8f), Color.Transparent) else listOf(Color.White.copy(alpha = 0.4f), Color.Transparent),
                        start = points.first(), end = points.last()
                    )
                    drawPath(path, brush, style = Stroke(width = width, pathEffect = PathEffect.dashPathEffect(floatArrayOf(10f, 10f), 0f)))
                }
            }
        }
    }

    fun DrawScope.drawCollisionMarkers(oracle: OracleResult?, scale: Float) {
        oracle?.collisions?.forEach { pos ->
            val s = 15f / scale
            val w = 3f / scale
            drawLine(Color.Red, Offset(pos.x - s, pos.y - s), Offset(pos.x + s, pos.y + s), w)
            drawLine(Color.Red, Offset(pos.x + s, pos.y - s), Offset(pos.x - s, pos.y + s), w)
        }
    }

    fun DrawScope.drawPlanets(
        bodies: List<SpaceBody>,
        stars: List<SpaceBody>,
        stats: TelemetryStats,
        pan: Offset,
        scale: Float,
        screenCenter: Offset,
        gameTimeNanos: Long,
        interpolationAlpha: Float = 1.0f,
        followedBodyId: Long? = null,
        followedBodyPos: Offset? = null
    ) {
        val blackHoles = stars.filter { it.isBlackHole }
        val activeStars = stars

        bodies.forEachIndexed { index, body ->
            val isFastest = index == stats.fastestBodyId
            val currentPos = if (body.id == followedBodyId && followedBodyPos != null) followedBodyPos else lerp(body.previousPosition, body.renderPosition, interpolationAlpha)
            val currentRotation = lerpRotation(body.previousRotation, body.rotation, interpolationAlpha)
            
            val relativePos = (currentPos + pan) * scale + screenCenter
            val margin = body.radius * 6f * scale
            val isOnScreen = relativePos.x > -margin && relativePos.x < size.width + margin &&
                           relativePos.y > -margin && relativePos.y < size.height + margin

            if (isFastest) {
                val speed = body.renderVelocity.getDistance()
                val velDir = if (speed > 0.1f) body.renderVelocity / speed else Offset.Zero
                val L = (speed * 0.4f).coerceIn(40f, 300f)
                val W = body.radius * 2.5f
                val pX = -velDir.y * W; val pY = velDir.x * W
                drawPath(Path().apply {
                    moveTo(currentPos.x, currentPos.y)
                    lineTo(currentPos.x - velDir.x * L + pX, currentPos.y - velDir.y * L + pY)
                    lineTo(currentPos.x - velDir.x * L - pX, currentPos.y - velDir.y * L - pY)
                    close()
                }, color = Color.Cyan.copy(alpha = 0.4f + 0.2f * ((sin(gameTimeNanos / 5e7).toFloat() + 1f) / 2f)))
                drawCircle(Brush.radialGradient(listOf(Color.White, Color.Cyan, Color.Transparent), center = currentPos, radius = body.radius * 4f), radius = body.radius * 4f, center = currentPos)
            }

            if (isOnScreen) {
                // Enforce minimum screen rendering size so small planets remain crisp and visible when zoomed out
                if (body.radius * scale < 2.0f) {
                    val minRenderRadius = (1.0f / scale).coerceAtLeast(body.radius)
                    drawCircle(body.color, minRenderRadius, currentPos)
                } else {
                    var nearestBH: SpaceBody? = null
                    var minBHDist = Float.MAX_VALUE
                    for (bh in blackHoles) {
                        val d = (bh.renderPosition - currentPos).getDistance()
                        if (d < minBHDist) { minBHDist = d; nearestBH = bh }
                    }
                    
                    val roche = nearestBH?.let { it.radius * 5f } ?: 0f
                    val isStretch = minBHDist < roche && minBHDist > 0f
                    val sF = if (isStretch) (roche / minBHDist).coerceIn(1f, 5.0f) else 1f
                    val sA = if (isStretch) (atan2((nearestBH!!.renderPosition.y - currentPos.y).toDouble(), (nearestBH!!.renderPosition.x - currentPos.x).toDouble()) * 180.0 / PI).toFloat() else 0f

                    var nearestS: SpaceBody? = null
                    var minSDist = Float.MAX_VALUE
                    for (s in activeStars) {
                        val d = (s.renderPosition - currentPos).getDistance()
                        if (d < minSDist) { minSDist = d; nearestS = s }
                    }

                    withTransform({
                        if (isStretch) { rotate(sA, currentPos); scale(sF, 1f / sqrt(sF), currentPos) }
                    }) {
                        withTransform({ rotate(currentRotation, currentPos) }) {
                            drawCircle(body.color, body.radius, currentPos)
                            drawCircle(Brush.sweepGradient(sweepGradientColors, center = currentPos), body.radius, currentPos)
                        }

                        if (nearestS != null) {
                            val sPos = lerp(nearestS.previousPosition, nearestS.renderPosition, interpolationAlpha)
                            val d = sPos - currentPos
                            val shadowC = currentPos + (if (minSDist > 0) d / minSDist else Offset.Zero) * (body.radius * 0.4f)
                            drawCircle(Brush.radialGradient(listOf(Color.Transparent, Color.Black.copy(alpha = 0.85f)), center = shadowC, radius = (body.radius * 1.3f).coerceAtLeast(1f)), body.radius, currentPos)
                            val highlightO = if (minSDist > 0) (d / minSDist) * (body.radius * 0.5f) else Offset.Zero
                            drawCircle(Brush.radialGradient(flareWhiteColors, center = currentPos + highlightO, radius = (body.radius * 1.1f).coerceAtLeast(1f)), body.radius, currentPos)
                        } else {
                            drawCircle(Brush.radialGradient(listOf(body.color.copy(alpha = 0.25f), body.color.copy(alpha = 0.05f), Color.Transparent), center = currentPos, radius = (body.radius * 1.4f).coerceAtLeast(1f)), body.radius * 1.4f, currentPos)
                            drawCircle(Brush.radialGradient(flareWhiteColors, center = currentPos + Offset(-body.radius * 0.3f, -body.radius * 0.3f), radius = (body.radius * 1.1f).coerceAtLeast(1f)), body.radius, currentPos)
                        }

                        if (body.hasRings) {
                            val ringRng = Random(body.id)
                            val ringColor = when {
                                ringRng.nextFloat() < 0.02f -> Color(0xFFFFD700).copy(alpha = 0.25f) // Gold
                                ringRng.nextFloat() < 0.20f -> Color.Gray.copy(alpha = 0.2f)        // Dusty
                                else -> Color.White.copy(alpha = 0.15f)                           // Icy
                            }
                            
                            withTransform({ rotate(45f + body.rotation * 0.5f, currentPos); scale(1.0f, 0.4f, currentPos) }) {
                                drawCircle(ringColor, radius = body.radius * 2.4f, center = currentPos, style = Stroke(width = body.radius * 0.3f))
                                if (ringRng.nextFloat() < 0.3f) {
                                    drawCircle(ringColor.copy(alpha = ringColor.alpha * 0.5f), radius = body.radius * 2.8f, center = currentPos, style = Stroke(width = body.radius * 0.1f))
                                }
                            }
                        }
                        if (body.hasLife) drawCircle(Color.Green.copy(alpha = (0.05f + 0.35f * body.lifeProgress).coerceIn(0f, 0.4f)), body.radius * 2.5f, currentPos)
                    }
                }
            } else drawCircle(body.color, body.radius, currentPos)
        }
    }


    fun DrawScope.drawWormholes(wormholes: List<Wormhole>, gameTimeNanos: Long, scale: Float) {
        wormholes.forEach { w ->
            val p = (sin(gameTimeNanos / 4e8).toFloat() + 1f) / 2f
            drawCircle(w.color.copy(alpha = 0.4f + 0.2f * p), w.radius, w.position, style = Stroke(width = 8f / scale))
            withTransform({ rotate(w.exitAngle, w.position) }) {
                drawPath(Path().apply {
                    moveTo(w.position.x + w.radius * 0.7f, w.position.y)
                    lineTo(w.position.x + w.radius * 0.3f, w.position.y - w.radius * 0.25f)
                    lineTo(w.position.x + w.radius * 0.3f, w.position.y + w.radius * 0.25f)
                    close()
                }, color = w.color.copy(alpha = 0.7f))
            }
        }
    }

    fun DrawScope.drawSurfaceIndicators(
        stars: List<SpaceBody>,
        obsWorldPos: Offset,
        totalRotation: Float,
        skyScale: Float,
        horizonY: Float,
        screenCenter: Offset,
        gameTimeNanos: Long,
        interpolationAlpha: Float = 1.0f
    ) {
        val rotRad = ((-totalRotation - 90f) * PI / 180.0)
        val cosR = cos(rotRad).toFloat()
        val sinR = sin(rotRad).toFloat()

        stars.forEach { star ->
            val currentPos = lerp(star.previousPosition, star.renderPosition, interpolationAlpha)
            val diff = (currentPos - obsWorldPos) * skyScale

            val rotX = diff.x * cosR - diff.y * sinR
            val rotY = diff.x * sinR + diff.y * cosR

            val starScreenX = screenCenter.x + rotX
            val starScreenY = horizonY + rotY

            val isOffScreen = starScreenX < 0 || starScreenX > size.width || starScreenY < 0 || starScreenY > size.height || starScreenY >= horizonY
            if (isOffScreen) {
                val angle = atan2(starScreenY - screenCenter.y, starScreenX - screenCenter.x)
                val color = if (star.isBlackHole) Color(0xFF8B00FF) else Color.Yellow
                val indicatorCenter = Offset(
                    (cos(angle) * (size.width / 2f - 90f)) + size.width / 2f,
                    (sin(angle) * (size.height / 2f - 90f)) + size.height / 2f
                )
                val pulse = (sin(gameTimeNanos.toDouble() / 150_000_000.0).toFloat() + 1f) / 2f
                drawCircle(color, 15f * 0.6f, indicatorCenter)
                drawCircle(color.copy(alpha = 0.3f + 0.3f * pulse), (25f) + (15f * pulse), indicatorCenter, style = Stroke(width = 3f))
            }
        }
    }



    fun DrawScope.drawIndicators(stars: List<SpaceBody>, bodies: List<SpaceBody>, effects: List<VisualEffect>, stats: TelemetryStats, pan: Offset, scale: Float, screenCenter: Offset, gameTimeNanos: Long, interpolationAlpha: Float = 1.0f) {
        stars.forEach { star ->
            val currentPos = lerp(star.previousPosition, star.renderPosition, interpolationAlpha)
            val relPos = (currentPos + pan) * scale + screenCenter
            if (relPos.x < 0 || relPos.x > size.width || relPos.y < 0 || relPos.y > size.height) {
                val angle = atan2(currentPos.y + pan.y, currentPos.x + pan.x)
                val color = if (star.isBlackHole) Color(0xFF8B00FF) else Color.Yellow
                val indicatorCenter = Offset((cos(angle) * (size.width / 2f - 90f)) + size.width / 2f, (sin(angle) * (size.height / 2f - 90f)) + size.height / 2f)
                val pulse = (sin(gameTimeNanos.toDouble() / 150_000_000.0).toFloat() + 1f) / 2f
                drawCircle(color, 15f * 0.6f, indicatorCenter)
                drawCircle(color.copy(alpha = 0.3f + 0.3f * pulse), (25f) + (15f * pulse), indicatorCenter, style = Stroke(width = 3f))
            }
        }
        bodies.forEachIndexed { idx, body ->
            val currentPos = lerp(body.previousPosition, body.renderPosition, interpolationAlpha)
            val relPos = (currentPos + pan) * scale + screenCenter
            if (relPos.x < 0 || relPos.x > size.width || relPos.y < 0 || relPos.y > size.height) {
                val angle = atan2(currentPos.y + pan.y, currentPos.x + pan.x)
                val isFastest = idx == stats.fastestBodyId
                val isLargest = idx == stats.largestBodyId
                val indicatorPos = Offset((cos(angle) * (size.width / 2f - 75f)) + size.width / 2f, (sin(angle) * (size.height / 2f - 75f)) + size.height / 2f)

                if (isFastest || isLargest) {
                    val pulse = 1.0f + 0.3f * sin(gameTimeNanos.toDouble() / 1e8).toFloat()
                    val color = if (isFastest && isLargest) {
                        if ((gameTimeNanos / 5e8).toLong() % 2 == 0L) Color.Cyan else Color(0xFFFF8C00)
                    } else if (isFastest) Color.Cyan else Color(0xFFFF8C00)

                    withTransform({ translate(indicatorPos.x, indicatorPos.y); scale(pulse, pulse, Offset.Zero); rotate((gameTimeNanos / 1e7).toFloat() % 360f, Offset.Zero) }) {
                        if (isFastest) {
                            // Diamond
                            val s = 12f
                            drawPath(Path().apply { moveTo(0f, -s); lineTo(s, 0f); lineTo(0f, s); lineTo(-s, 0f); close() }, color = color)
                        } else {
                            // Hexagon
                            val s = 12f; val h = s * 0.866f
                            drawPath(Path().apply { moveTo(s, 0f); lineTo(s/2, h); lineTo(-s/2, h); lineTo(-s, 0f); lineTo(-s/2, -h); lineTo(s/2, -h); close() }, color = color)
                        }
                    }
                    drawCircle(color.copy(alpha = 0.2f), radius = 25f * pulse, center = indicatorPos, style = Stroke(width = 2f))
                } else {
                    drawCircle(body.color.copy(alpha = 0.5f), 8f, indicatorPos)
                }
            }
        }

        effects.forEach { effect ->
            if (effect.type == EffectType.OFFSCREEN_MERGER) {
                val elapsed = gameTimeNanos / 1_000_000 - effect.startTime
                val progress = (elapsed.toFloat() / effect.durationMs.coerceAtLeast(1)).coerceIn(0f, 1f)
                val fade = (1.0f - progress).coerceIn(0f, 1f)
                val relPos = (effect.position + pan) * scale + screenCenter
                if (relPos.x < 0 || relPos.x > size.width || relPos.y < 0 || relPos.y > size.height) {
                    val angle = atan2(relPos.y - screenCenter.y, relPos.x - screenCenter.x)
                    val indicatorPos = Offset((cos(angle) * (size.width / 2f - 60f)) + size.width / 2f, (sin(angle) * (size.height / 2f - 60f)) + size.height / 2f)
                    drawCircle(color = Color.Red.copy(alpha = fade * 0.8f), radius = (progress * 150f).coerceIn(1f, 200f), center = indicatorPos, style = Stroke(width = 4f))
                }
            }
        }
    }

    fun DrawScope.drawNebulae(nebulae: List<Nebula>, gameTimeNanos: Long) {
        nebulae.forEach { n ->
            drawCircle(
                brush = Brush.radialGradient(
                    0.0f to n.color.copy(alpha = 0.35f),
                    1.0f to Color.Transparent,
                    center = n.position,
                    radius = n.radius
                ),
                radius = n.radius,
                center = n.position
            )
        }
    }

    fun DrawScope.drawSurfaceHorizon(body: SpaceBody, pitchAngle: Float = 45f) {
        val horizonY = size.height / 2f + (pitchAngle / 90f) * (size.height * 0.5f)

        if (horizonY > 0f) {
            // Upper Atmospheric Glow (radiates upward into space)
            drawRect(
                brush = Brush.verticalGradient(
                    0.0f to Color.Transparent,
                    0.5f to body.color.copy(alpha = 0.15f),
                    1.0f to body.color.copy(alpha = 0.4f),
                    startY = (horizonY - 150f).coerceAtLeast(0f),
                    endY = horizonY
                ),
                topLeft = Offset(0f, (horizonY - 150f).coerceAtLeast(0f)),
                size = Size(size.width, 150f)
            )
        }

        if (horizonY < size.height + 50f) {
            // Opaque Curved Ground Surface (occludes objects below horizon)
            val groundPath = Path().apply {
                moveTo(0f, size.height + 20f)
                lineTo(0f, horizonY + 15f)
                quadraticTo(size.width / 2f, horizonY - 15f, size.width, horizonY + 15f)
                lineTo(size.width, size.height + 20f)
                close()
            }

            drawPath(
                path = groundPath,
                brush = Brush.verticalGradient(
                    listOf(body.color.copy(alpha = 0.95f), Color(0xFF0A0A10))
                )
            )

            drawPath(
                path = groundPath,
                color = Color.White.copy(alpha = 0.4f),
                style = Stroke(width = 3f)
            )
        }
    }

    private fun Offset.getDistance() = sqrt((x * x + y * y).toDouble()).toFloat()
}
